/** Admin subscribe: email + optional trial / comp (TMMPDM-0008). */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isValidationFailed, validationFields } from '@/api/errors';
import type { AdminSubscribeRequest, AdminSubscribeResponse } from '@/api/tmm/types';
import { AppSwitch } from '@/components/ui/AppSwitch';
import { notPricedCode, TenantNotPricedAlert, type NotPricedCode } from './TenantNotPricedAlert';

interface SubscribeDialogProps {
  open: boolean;
  slug: string;
  resellerId: number | null;
  resellerName?: string | null | undefined;
  /** The tenant's currency, for TENANT_CURRENCY_NOT_PRICED (TMMPDM-0025). */
  currency?: string | undefined;
  onClose: () => void;
  onDone: (result: AdminSubscribeResponse) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function toDateTime(date: string): string {
  return date.includes('T') ? date : `${date}T00:00:00.000Z`;
}

export function SubscribeDialog({
  open,
  slug,
  resellerId,
  resellerName,
  currency,
  onClose,
  onDone,
}: SubscribeDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {open ? (
        <SubscribeForm
          key={slug}
          slug={slug}
          resellerId={resellerId}
          resellerName={resellerName}
          currency={currency}
          onClose={onClose}
          onDone={onDone}
        />
      ) : null}
    </Dialog>
  );
}

function SubscribeForm({
  slug,
  resellerId,
  resellerName,
  currency,
  onClose,
  onDone,
}: Omit<SubscribeDialogProps, 'open'>) {
  const { t } = useCopy();
  const [email, setEmail] = useState('');
  const [trialDays, setTrialDays] = useState('');
  const [compOn, setCompOn] = useState(false);
  const [until, setUntil] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notPriced, setNotPriced] = useState<NotPricedCode | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const submit = async () => {
    const nextFields: Record<string, string> = {};
    const trimmed = email.trim();
    if (!trimmed) {
      nextFields.email = t('subscriptions.subscribeDialog.emailRequired', 'Email is required.');
    } else if (!EMAIL_RE.test(trimmed)) {
      nextFields.email = t('subscriptions.subscribeDialog.emailInvalid', 'Enter a valid email.');
    }
    let trial: number | undefined;
    if (trialDays.trim() !== '') {
      trial = Number(trialDays);
      if (!Number.isInteger(trial) || trial < 0) {
        nextFields.trialDays = t(
          'subscriptions.subscribeDialog.trialInvalid',
          'Must be zero or greater.',
        );
      }
    }
    if (Object.keys(nextFields).length > 0) {
      setFieldErrors(nextFields);
      return;
    }
    const body: AdminSubscribeRequest = {
      email: trimmed,
      ...(trial !== undefined ? { trialDays: trial } : {}),
      // TMMAPI-0167: Comp.until is optional. A blank date omits it; a chosen date sends that instant.
      ...(compOn ? { comp: until ? { until: toDateTime(until) } : {} } : {}),
    };
    setSubmitting(true);
    setFormError(null);
    setNotPriced(null);
    setFieldErrors({});
    try {
      onDone(await adminApi.subscribe(slug, body));
    } catch (error: unknown) {
      const notPricedAs = notPricedCode(error);
      if (notPricedAs) {
        setNotPriced(notPricedAs);
      } else if (error instanceof ApiError && isValidationFailed(error)) {
        const fields = validationFields(error, t);
        setFieldErrors(fields);
        // A field the form doesn't show is said once, in the banner; shown fields are said at the field.
        if (Object.keys(fields).some((field) => !['email', 'trialDays'].includes(field))) {
          setFormError(
            errorCopy(
              error,
              t,
              t('subscriptions.subscribeError', "Couldn't create the subscription."),
            ),
          );
        }
      } else {
        setFormError(
          errorCopy(
            error,
            t,
            t('subscriptions.subscribeError', "Couldn't create the subscription."),
          ),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogTitle>{t('subscriptions.subscribeDialog.title', 'Subscribe')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {notPriced ? (
            <TenantNotPricedAlert
              code={notPriced}
              resellerId={resellerId}
              resellerName={resellerName}
              currency={currency}
            />
          ) : null}
          {formError ? <Alert severity="error">{formError}</Alert> : null}
          <TextField
            label={t('subscriptions.subscribeDialog.email', 'Email')}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email}
            required
          />
          <TextField
            label={t('subscriptions.subscribeDialog.trialDays', 'Trial days')}
            value={trialDays}
            onChange={(event) => setTrialDays(event.target.value)}
            error={Boolean(fieldErrors.trialDays)}
            helperText={fieldErrors.trialDays}
            type="number"
            slotProps={{ htmlInput: { min: 0, step: 1 } }}
          />
          <AppSwitch
            label={t('subscriptions.subscribeDialog.comp', 'Comp')}
            checked={compOn}
            onChange={setCompOn}
          />
          {compOn ? (
            <TextField
              label={t('subscriptions.subscribeDialog.until', 'Comp until')}
              type="date"
              value={until}
              onChange={(event) => setUntil(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('subscriptions.subscribeDialog.cancel', 'Cancel')}</Button>
        <SubmitButton onClick={() => void submit()} busy={submitting}>
          {t('subscriptions.subscribeDialog.submit', 'Subscribe')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
